import { cConst, cGradient, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Celebration + movement library (plan §4.1 expansion). Fireworks is the
 * sub-emitter showcase: rockets carry ribbon trails, and their death events
 * trigger the peony burst, crackle stars and flash — three children off one
 * parent layer.
 */
/** Firework show: three rockets per loop, each detonating into peony + crackle + flash on death. */
export const fireworksEffect = {
    version: 1,
    meta: { name: 'Fireworks', duration: 2.6, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'fw_rocket',
            name: 'rockets',
            delay: 0.05,
            duration: 1.6,
            maxParticles: 6,
            emitter: {
                mode: { type: 'burst', count: 1, cycles: 3, interval: 0.75 },
                shape: { type: 'cone', angle: 5, radius: 0.35 },
                initial: {
                    lifetime: vRange(0.8, 0.95),
                    speed: vRange(6, 7.5),
                    spread: vConst(4),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cRange([1, 0.9, 0.6, 1], [1, 0.75, 0.45, 1]),
                },
            },
            renderer: {
                type: 'trail',
                points: 22,
                width: { preset: 'custom', points: [{ t: 0, v: 1, outTan: 0 }, { t: 1, v: 0, inTan: -2 }] },
                widthScale: 0.06,
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 4 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.85, [1, 0.9, 0.6, 0.9]], [1, [1, 0.8, 0.4, 0.4]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'fw_peony',
            name: 'peony burst',
            delay: 0,
            duration: 2.6,
            maxParticles: 128,
            emitter: {
                trigger: { parent: 'fw_rocket', on: 'death' },
                mode: { type: 'burst', count: 42 },
                shape: { type: 'sphere', radius: 0.05, shell: true },
                initial: {
                    lifetime: vRange(0.9, 1.4),
                    speed: vRange(3.2, 5),
                    spread: vConst(0),
                    size: vRange(0.05, 0.1),
                    rotation: vConst(0),
                    // smooth spectrum — stars sample a pink→violet→cyan wheel
                    color: cGradient(gradient('smooth', [0, [1, 0.5, 0.7, 1]], [0.35, [0.9, 0.55, 1, 1]], [0.7, [0.55, 0.7, 1, 1]], [1, [0.45, 0.95, 1, 1]])),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.6 },
                { type: 'gravity', enabled: true, strength: 2.6 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1.3, 1.3, 1.3, 1]], [0.5, [1, 1, 1, 1]], [0.85, [1, 0.9, 0.8, 0.6]], [1, [1, 0.7, 0.5, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'fw_crackle',
            name: 'crackle stars',
            delay: 0,
            duration: 2.6,
            maxParticles: 80,
            emitter: {
                trigger: { parent: 'fw_rocket', on: 'death' },
                mode: { type: 'burst', count: 24 },
                shape: { type: 'sphere', radius: 0.12 },
                initial: {
                    lifetime: vRange(0.5, 1.1),
                    speed: vRange(1.5, 3),
                    spread: vConst(0),
                    size: vRange(0.04, 0.09),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-200, 200),
                    color: cRange([1, 0.95, 0.7, 1], [1, 0.85, 0.5, 1]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.2 },
                { type: 'gravity', enabled: true, strength: 3.2 },
                {
                    // strobing gold crackle
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.3, [1, 1, 0.9, 0.15]], [0.45, [1, 1, 1, 1]], [0.62, [1, 0.95, 0.8, 0.1]], [0.75, [1, 1, 0.95, 0.9]], [1, [1, 0.8, 0.5, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'fw_flash',
            name: 'detonation flash',
            delay: 0,
            duration: 2.6,
            maxParticles: 8,
            emitter: {
                trigger: { parent: 'fw_rocket', on: 'death' },
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.18),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(1.6, 2),
                    rotation: vConst(0),
                    color: cConst([1, 0.97, 0.9, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.8, 0.6, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/** Treasure sparkle: slow gold glints and rising motes over a soft pulse — chest/rare-item shimmer. */
export const treasureSparkleEffect = {
    version: 1,
    meta: { name: 'Treasure Sparkle', duration: 2, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'tr_glints',
            name: 'star glints',
            delay: 0,
            duration: 2,
            maxParticles: 16,
            emitter: {
                mode: { type: 'rate', perSecond: 6 },
                shape: { type: 'box', size: [0.85, 0.5, 0.85] },
                initial: {
                    lifetime: vRange(0.55, 0.95),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.08, 0.19),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-100, 100),
                    color: cRange([1, 0.98, 0.85, 1], [1, 0.85, 0.45, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.35, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'tr_motes',
            name: 'rising motes',
            delay: 0,
            duration: 2,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 8 },
                shape: { type: 'box', size: [0.7, 0.3, 0.7] },
                initial: {
                    lifetime: vRange(0.8, 1.3),
                    speed: vRange(0.05, 0.15),
                    spread: vConst(0),
                    size: vRange(0.025, 0.06),
                    rotation: vConst(0),
                    color: cRange([1, 0.9, 0.55, 1], [1, 0.8, 0.4, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.8 },
                { type: 'turbulence', enabled: true, frequency: 1, amplitude: 0.25, scrollSpeed: 0.2 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.3, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'tr_glow',
            name: 'warm pulse',
            delay: 0,
            duration: 0.05,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cConst([1, 0.85, 0.45, 0.5]),
                },
            },
            modules: [
                {
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.85, outTan: 0.6 }, { t: 0.5, v: 1.1, inTan: 0, outTan: 0 }, { t: 1, v: 0.85, inTan: -0.6 }] },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.75]], [0.5, [1.15, 1.1, 1, 1]], [1, [1, 1, 1, 0.75]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/** Teleport: energy implodes inward, snaps into a light pillar, then blooms back out — arrive or depart. */
export const teleportEffect = {
    version: 1,
    meta: { name: 'Teleport', duration: 1.5, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'tp_gather',
            name: 'imploding energy',
            delay: 0,
            duration: 0.4,
            maxParticles: 48,
            emitter: {
                mode: { type: 'rate', perSecond: 90 },
                shape: { type: 'ring', radius: 1.25, direction: 'inward' },
                initial: {
                    lifetime: vRange(0.3, 0.4),
                    speed: vRange(3, 4),
                    spread: vConst(6),
                    size: vRange(0.05, 0.12),
                    rotation: vConst(0),
                    color: cRange([0.6, 0.95, 1, 1], [0.4, 0.6, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.35, 0.6) },
            modules: [
                { type: 'attractor', enabled: true, position: [0, 0, 0], strength: 6 },
                { type: 'vortex', enabled: true, strength: 3 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.3, [1, 1, 1, 1]], [1, [1.2, 1.2, 1.2, 1]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'tp_flash',
            name: 'snap flash',
            delay: 0.45,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.22),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.6),
                    rotation: vConst(0),
                    color: cConst([0.92, 0.98, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.5, 0.7, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'tp_pillar',
            name: 'light pillar',
            delay: 0.45,
            duration: 0.04,
            maxParticles: 26,
            emitter: {
                mode: { type: 'burst', count: 18 },
                shape: { type: 'ring', radius: 0.22, direction: 'up' },
                initial: {
                    lifetime: vRange(0.3, 0.55),
                    speed: vRange(4.5, 8),
                    spread: vConst(3),
                    size: vRange(0.06, 0.14),
                    rotation: vConst(0),
                    color: cRange([0.9, 1, 1, 1], [0.55, 0.8, 1, 1]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.45, 0.7) },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.2 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 1, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'tp_ring',
            name: 'arrival ring',
            delay: 0.48,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.45),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.2),
                    rotation: vConst(0),
                    color: cConst([0.65, 0.85, 1, 0.95]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 64,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.7, outTan: 0 }, { t: 1, v: 0.1, inTan: -0.9 }] },
                    widthScale: 0.3,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [0.4, 0.65, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'tp_afterglow',
            name: 'afterglow motes',
            delay: 0.5,
            duration: 0.15,
            maxParticles: 24,
            emitter: {
                mode: { type: 'burst', count: 14 },
                shape: { type: 'sphere', radius: 0.5 },
                initial: {
                    lifetime: vRange(0.5, 0.9),
                    speed: vRange(0.3, 0.8),
                    spread: vConst(0),
                    size: vRange(0.04, 0.1),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-150, 150),
                    color: cRange([0.85, 0.95, 1, 1], [0.6, 0.8, 1, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.9 },
                { type: 'drag', enabled: true, coefficient: 1 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
    ],
};
/** Landing poof: a squashy dust donut with grit chips and a quick ground ring — platformer jump-land. */
export const landingPoofEffect = {
    version: 1,
    meta: { name: 'Landing Poof', duration: 0.8, loop: false, simulationSpace: 'world' },
    layers: [
        defaultLayer({
            id: 'lp_ring',
            name: 'ground ring',
            delay: 0,
            duration: 0.04,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.3),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.1),
                    rotation: vConst(0),
                    color: cConst([0.9, 0.85, 0.75, 0.8]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 48,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.7, outTan: 0 }, { t: 1, v: 0.12, inTan: -0.8 }] },
                    widthScale: 0.18,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'lp_puffs',
            name: 'dust donut',
            delay: 0,
            duration: 0.05,
            maxParticles: 14,
            emitter: {
                mode: { type: 'burst', count: 9 },
                shape: { type: 'ring', radius: 0.28 },
                initial: {
                    lifetime: vRange(0.35, 0.6),
                    speed: vRange(1.3, 2.3),
                    spread: vConst(12),
                    size: vRange(0.26, 0.45),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-70, 70),
                    color: cRange([0.92, 0.88, 0.8, 0.9], [0.78, 0.72, 0.62, 0.9]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 3.5 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'lp_grit',
            name: 'grit chips',
            delay: 0,
            duration: 0.05,
            maxParticles: 10,
            emitter: {
                mode: { type: 'burst', count: 6 },
                shape: { type: 'cone', angle: 50, radius: 0.2 },
                initial: {
                    lifetime: vRange(0.3, 0.5),
                    speed: vRange(1.8, 3.4),
                    spread: vConst(10),
                    size: vRange(0.04, 0.09),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-600, 600),
                    color: cRange([0.7, 0.62, 0.5, 1], [0.5, 0.44, 0.36, 1]),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'shard' } },
            modules: [
                { type: 'gravity', enabled: true, strength: 14 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.5 } },
        }),
    ],
};
